/**
 * Типизированные измерения.
 *
 * CLAUDE.md: единицы имперские, никаких безымянных числовых полей.
 * Через год никто не вспомнит, 6 — это 6/32 дюйма или 6 миллиметров.
 *
 * Числовой замер закрывает холодный старт: правило «< 4/32 → критично»
 * работает в первый день, без единого байта истории. Тренд появляется сам.
 */
import { z } from 'zod';
export const MEASUREMENT_KINDS = [
    'tread_depth_32nds',
    'tire_pressure_psi',
    'pushrod_stroke_in',
    'brake_lining_32nds',
];
export const MEASUREMENT_SPECS = {
    tread_depth_32nds: {
        kind: 'tread_depth_32nds',
        unit: '/32"',
        label: 'Tread depth',
        worse: 'lower',
        min: 0,
        max: 32,
        step: 1,
        decimals: 0,
    },
    tire_pressure_psi: {
        kind: 'tire_pressure_psi',
        unit: 'psi',
        label: 'Tire pressure',
        worse: 'lower',
        min: 0,
        max: 160,
        step: 1,
        decimals: 0,
    },
    pushrod_stroke_in: {
        kind: 'pushrod_stroke_in',
        unit: '"',
        label: 'Brake pushrod stroke',
        worse: 'higher',
        min: 0,
        max: 4,
        step: 0.25,
        decimals: 2,
    },
    brake_lining_32nds: {
        kind: 'brake_lining_32nds',
        unit: '/32"',
        label: 'Brake lining thickness',
        worse: 'lower',
        min: 0,
        max: 32,
        step: 1,
        decimals: 0,
    },
};
export const measurementKindSchema = z.enum(MEASUREMENT_KINDS);
export const measurementSchema = z.object({
    kind: measurementKindSchema,
    value: z.number(),
    /** Позиция обязательна: порог зависит от неё, а не только от типа узла. */
    position: z.string().min(1).max(64),
});
export function isMeasurementInRange(kind, value) {
    const spec = MEASUREMENT_SPECS[kind];
    return Number.isFinite(value) && value >= spec.min && value <= spec.max;
}
export const LEGAL_LIMITS = [
    // 49 CFR 393.75(c): рулевая ось — 4/32", остальные — 2/32".
    { kind: 'tread_depth_32nds', appliesTo: 'steer', limit: 4, citation: '49 CFR 393.75(c)(1)' },
    { kind: 'tread_depth_32nds', appliesTo: 'drive', limit: 2, citation: '49 CFR 393.75(c)(2)' },
    { kind: 'tread_depth_32nds', appliesTo: 'trailer', limit: 2, citation: '49 CFR 393.75(c)(2)' },
    { kind: 'tread_depth_32nds', appliesTo: 'lift', limit: 2, citation: '49 CFR 393.75(c)(2)' },
    // 49 CFR 393.47(e): предел хода штока зависит от типа камеры.
    { kind: 'pushrod_stroke_in', appliesTo: 'type_16', limit: 1.75, citation: '49 CFR 393.47(e)' },
    { kind: 'pushrod_stroke_in', appliesTo: 'type_20', limit: 1.75, citation: '49 CFR 393.47(e)' },
    { kind: 'pushrod_stroke_in', appliesTo: 'type_24', limit: 1.75, citation: '49 CFR 393.47(e)' },
    {
        kind: 'pushrod_stroke_in',
        appliesTo: 'type_24_long',
        limit: 2.0,
        citation: '49 CFR 393.47(e)',
    },
    { kind: 'pushrod_stroke_in', appliesTo: 'type_30', limit: 2.0, citation: '49 CFR 393.47(e)' },
    {
        kind: 'pushrod_stroke_in',
        appliesTo: 'type_30_long',
        limit: 2.5,
        citation: '49 CFR 393.47(e)',
    },
];
export function legalLimitFor(kind, appliesTo) {
    return LEGAL_LIMITS.find((l) => l.kind === kind && l.appliesTo === appliesTo);
}
/**
 * Порог флота допустим, только если он не мягче закона.
 * Мягкий порог означал бы, что система молчит при нарушении, которое
 * инспектор на дороге увидит как out-of-service.
 */
export function isThresholdLegal(kind, appliesTo, threshold) {
    const legal = legalLimitFor(kind, appliesTo);
    if (legal === undefined)
        return true;
    return MEASUREMENT_SPECS[kind].worse === 'lower'
        ? threshold >= legal.limit
        : threshold <= legal.limit;
}
//# sourceMappingURL=measurement.js.map